import styled from "@emotion/styled";
import { styled as muiStyled, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { RJSFSchema, UiSchema } from "@rjsf/utils";
import type { JSONSchema7Type } from "json-schema";
import BreadcrumbNode from "@/components/BreadcrumbNode";
import type { DataListProps } from "@/components/DataList/DataList";
import DataList from "@/components/DataList/DataList";
import type { DataListRow } from "@/components/DataList/DataListRow";
import SimpleDialog from "@/components/SimpleDialog/SimpleDialog";
import type { SimpleDialogListRow } from "@/components/SimpleDialog/SimpleDialogListRow";
import type { ErrorResponse } from "@/declarations/models/ErrorResponse";
import type { KioUser } from "@/declarations/models/KioUser";
import type { Role } from "@/declarations/models/Role";
import type { PaginationResult } from "@/declarations/PaginationResult";
import { schema } from "@/declarations/schemas/user/schema";
import { uiSchema } from "@/declarations/schemas/user/uiSchema";
import KioForm from "@/framework/KioForm/KioForm";
import Loader from "@/framework/Loader";
import { useMessenger } from "@/framework/Messenger/Messenger";
import Api from "@/services/Api";
import type { InstanceUserRole } from "@/declarations/models/InstanceUserRole";
import type { ApplicationInstance } from "@/declarations/models/ApplicationInstance";
import { resolvePotentiallyLocalizedString } from "@/utils/obj";
import Settings from "@/Settings";

const Container = muiStyled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

const ErrorText = styled(Typography)``;

export const SingleUserView: FC = () => {
  const { t } = useTranslation("common");
  const getPotentiallyLocalizedString = resolvePotentiallyLocalizedString(Settings.DEFAULT_LOCALE);
  const { user_id } = useParams<{ user_id: string }>();
  const history = useNavigate();
  const { success, error, warning } = useMessenger();
  const title = t(uiSchema["ui:title"]);
  if (title) document.title = title;

  const [user, setUser] = useState<KioUser | null>(null);
  const [responseError, setResponseError] = useState<ErrorResponse | string | null>(null);
  const [userInstances, setUserInstances] = useState<ApplicationInstance[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInstanceUserRole, setSelectedInstanceUserRole] = useState<InstanceUserRole>();
  const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState(Date.now());

  const editSchema: RJSFSchema = JSON.parse(JSON.stringify(schema));
  editSchema["properties"] = {
    ...editSchema["properties"],
    username: {
      format: "email",
      type: "string",
      readOnly: true,
    },
  };

  const handleDialogClose = async (selection: Role) => {
    const userId = parseInt(user_id as string);
    if (userId && selectedInstanceUserRole?.user_role !== selection) {
      Api.updateRoleToUsersInstance(userId, selectedInstanceUserRole?.application_instance_id || 0, selection)
        .fetch()
        .catch((e) => console.error(e));
    }
    setDialogOpen(false);
    setLastFetchedTimestamp(Date.now);
  };

  const deleteUserFromInstance = async (instanceUserRole: InstanceUserRole) => {
    const [_, error] = await Api.removeUserFromInstance(
      instanceUserRole.user_id,
      instanceUserRole.application_instance_id
    ).fetch();
    if (error) {
      return Promise.reject("Unable to remove user from instance");
    }
    setLastFetchedTimestamp(Date.now());
  };

  const editUserInstanceRole = (instanceUserRole: InstanceUserRole) => {
    setSelectedInstanceUserRole(instanceUserRole);
    setDialogOpen(true);
  };

  const userFormData: KioUser = {
    id: user?.id ? user.id : undefined,
    name: user?.name ? user.name : "",
    username: user?.username ? user.username : "",
    is_admin: user?.is_admin ? user.is_admin : false,
    image_url: user?.image_url ? user.image_url : undefined,
    activated: user?.activated ? user.activated : false,
    is_banned: user?.is_banned ? user.is_banned : false,
    updated_at: user?.updated_at || "",
    created_at: user?.created_at || "",
  };

  const addInstanceSchema: JSONSchema7Type = {
    type: "object",
    required: ["application_instance_id", "role"],
    properties: {
      application_instance_id: {
        type: "integer",
      },
      role: {
        type: "string",
        enum: roles,
        enumNames: roles.map((role) => t(`userRole.${role}`)),
      },
    },
  };

  const addInstanceUiSchema: UiSchema = {
    "ui:title": "views.singleUserView.addInstance",
    application_instance_id: {
      "ui:title": "generic.instance",
      "ui:widget": "EntityAutocompleteWidget",
      "ui:options": {
        type: "ApplicationInstance",
        valueField: "id",
        displayField: "name",
      },
    },
    role: {
      "ui:title": "views.singleUserView.role",
    },
  };

  const addInstanceOnSubmit = (evt: any): void => {
    if (evt.role && evt.application_instance_id) {
      if (!userInstances.some((l) => l.id === evt.application_instance_id)) {
        const userId: number = parseInt(user_id as string);
        const role = evt.role as Role;
        const instanceId: number = parseInt(evt.application_instance_id);

        Api.addUserToInstance(userId, instanceId, role)
          .fetch()
          .then(() => {
            setLastFetchedTimestamp(Date.now);
          });
      } else warning("Instance already added");
    } else warning("Missing data");
  };

  const submitUpdatedUser = async (user: KioUser) => {
    const updatedUser = await Api.updateUser(user).fetchDirect(null);
    if (updatedUser) {
      success("generic.saved");
    } else {
      error("generic.saveFailed");
    }
  };

  const getItems: DataListProps<InstanceUserRole>["getItems"] = (page: number, pageSize: number) => {
    const ctx = Api.getAllInstanceUserRoles({ user_id: parseInt(user_id as string), page, page_size: pageSize });
    const defaultValue: PaginationResult<InstanceUserRole> = {
      page,
      page_size: pageSize,
      count: 0,
      total_count: 0,
      items: [],
    };
    return ctx.fetchDirect(defaultValue);
  };

  const mapperFn = (item: InstanceUserRole): DataListRow => ({
    key: String(item.application_instance_id),
    title: `${item.application_instance_name} - ${getPotentiallyLocalizedString(item.application_instance_owner_name)}`,
    infoText: `Id: ${item.application_instance_id}`,
    chipContent: t(`userRole.${item.user_role}`),
  });

  const mapperFnDialog = (roleItem: Role): SimpleDialogListRow => ({
    key: roleItem,
    title: t(`userRole.${roleItem}`),
  });

  const onItemClick = (item: InstanceUserRole) => {
    history(`/admin/instance/${item.application_instance_id}`);
  };

  // Fetch user data
  useEffect(() => {
    const getUser = Api.getOneUser(parseInt(user_id as string));
    getUser.fetch().then(([res, err]) => {
      if (err) {
        setResponseError(err);
        return;
      }
      if (res) setUser(res);
    });
    return () => {
      getUser.abort();
    };
  }, [setUser, user_id]);

  useEffect(() => {
    const defaultValue: PaginationResult<Role> = { page: 0, page_size: 10000, count: 0, total_count: 0, items: [] };
    const getRoles = Api.getAllRoles();
    getRoles.fetchDirect(defaultValue).then((res) => setRoles(res.items));
    return () => getRoles.abort();
  }, [setRoles]);

  useEffect(() => {
    const getUserInstances = Api.getAllInstances({ user_id: parseInt(user_id as string) });
    getUserInstances.fetch().then(([res, err]) => {
      if (err) {
        setResponseError(err);
        return;
      }
      if (res) setUserInstances(res.items);
    });
    return () => getUserInstances.abort();
  }, [lastFetchedTimestamp, user_id]);

  return (
    <Container>
      <BreadcrumbNode label={`${user?.name || user_id}`} />
      {!user && !responseError ? (
        <Loader loadingText={t("views.singleUserView.loadingUser")} />
      ) : user ? (
        <>
          <KioForm
            formData={userFormData}
            schema={editSchema}
            uiSchema={uiSchema}
            onSubmit={submitUpdatedUser}
            onCancel={() => history(-1)}
          />
          <KioForm schema={addInstanceSchema} uiSchema={addInstanceUiSchema} onSubmit={addInstanceOnSubmit} />
        </>
      ) : (
        <ErrorText color="error">{JSON.stringify(responseError)}</ErrorText>
      )}

      <DataList
        listTitle={t("views.singleUserView.userInstances")}
        getItems={getItems}
        mapperFn={mapperFn}
        onItemClick={onItemClick}
        onDeleteItem={deleteUserFromInstance}
        onEditItem={editUserInstanceRole}
        disableViewTypeSelection
        externalDataChanged={lastFetchedTimestamp}
        disableSorting
      />

      <SimpleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        data={roles}
        title={`${t("views.singleUserView.dialogBoxTitle")}: ${selectedInstanceUserRole?.application_instance_name}`}
        selectedValue={selectedInstanceUserRole?.user_role}
        mapperFn={mapperFnDialog}
      />
    </Container>
  );
};

export default SingleUserView;
